<template>
	<form class="bank-account-form" @submit.prevent>
		<fieldset>
			<legend>{{ sectionName }}</legend>
			<vs-alert color="success" v-if="isLocalCurrencyWallet && hasBanner">
				<div>
					<div class="mr-5">
						<info-icon size="1.5x"></info-icon>
					</div>
					<div>
						Your funds will be settled to your bank account by
						Triple-A once your account balance reaches the threshold
						of <span style="font-weight: 700">USD 3000</span> or
						equivalent, unless otherwise stated.
					</div>
				</div>
			</vs-alert>
			<ul v-if="isLocalCurrencyWallet">
				<li>
					<label>
						Account name
						<span class="req" v-if="bankAccount.accountName.isRequired">*</span>
						<span class="tooltip">
							<info-icon size="1x"></info-icon>
							<span>
								Please ensure your bank account is registered under your legal entity's name.
							</span>
						</span>
						<input
							type="text"
							name="accountName"
							ref="accountName"
							maxlength="64"
							v-model="bankAccount.accountName.value"
							:placeholder="`Ex: ${legalEntityName}`"
							:class="{
								'input-error-style': bankAccount.accountName.error,
							}" />
						<span class="indicator">{{ `${bankAccount.accountName.value.length}/64` }}</span>
						<span
							v-if="bankAccount.accountName.error"
							class="error">Please insert an account name.</span>
					</label>
					<p class="caption">
						Please ensure your bank account is registered under your legal entity's name.
					</p>
					<p v-if="legalEntityName" class="caption">
						The current entity name registered in our system for this merchant is:
						<span class="tooltip" @click="copyText(legalEntityName)" :enabled="copiedValue === legalEntityName">
							{{ legalEntityName }}
							<copy-icon size="1x"></copy-icon>
							<span v-if="copiedValue === legalEntityName">Copied</span>
						</span>
					</p>
				</li>

				<li>
					<label>
						Account number / IBAN
						<span class="req" v-if="bankAccount.accountNumber.isRequired">*</span>
						<input
							type="text"
							name="accountNumber"
							ref="accountNumber"
							v-model="bankAccount.accountNumber.value"
							:class="{
								'input-error-style': bankAccount.accountNumber.error,
							}" />
						<span
							v-if="bankAccount.accountNumber.error"
							class="error">Please insert a valid account number.</span>
					</label>
				</li>

				<li>
					<label>
						Bank Country
						<span class="req" v-if="bankAccount.bankCountry.isRequired">*</span>
						<select
							id="bankCountrySelection"
							v-model="bankAccount.bankCountry.value"
							class="account-selection style-select"
							:class="{ 'input-error-style': bankAccount.bankCountry.error }">
							<option
								v-for="item in countries"
								:value="item.code"
								:key="item.code">
								{{ item.name }} ({{ item.code }})
							</option>
						</select>
						<span
							v-if="bankAccount.bankCountry.error"
							class="error">Please select a country.</span>
					</label>
				</li>

				<li>
					<label>
						SWIFT Code
						<span class="req" v-if="bankAccount.swiftCode.isRequired">*</span>
						<input
							type="text"
							name="swiftCode"
							ref="swiftCode"
							v-model="bankAccount.swiftCode.value"
							:class="{ 'input-error-style': bankAccount.swiftCode.error }"
							maxlength="11" />
						<span
							v-if="bankAccount.swiftCode.error"
							class="error">Please insert a valid BIC/SWIFT code.</span>
					</label>
				</li>

				<li v-if="bankAccount.bankCountry.value === 'US'">
					<label>
						ABA Routing Number
						<span class="req" v-if="bankAccount.routingNumber.isRequired">*</span>
						<input
							type="text"
							name="routingNumber"
							ref="routingNumber"
							v-model="bankAccount.routingNumber.value"
							:class="{
								'input-error-style': bankAccount.routingNumber.error,
							}" />
						<span
							v-if="bankAccount.routingNumber.error"
							class="error">Please insert a routing number (required for US bank
							transfer).</span>
					</label>
				</li>

				<li>
					<label>
						Bank name
						<span class="req" v-if="bankAccount.bankName.isRequired">*</span>
						<input
							type="text"
							name="bankName"
							ref="bankName"
							v-model="bankAccount.bankName.value"
							:class="{ 'input-error-style': bankAccount.bankName.error }" />
						<span
							v-if="bankAccount.bankName.error"
							class="error">Please insert a valid bank name.</span>
					</label>
				</li>

				<li>
					<label>
						Bank address
						<span class="req" v-if="bankAccount.bankAddress.isRequired">*</span>
						<textarea
							name="bankAddress"
							ref="bankAddress"
							v-model="bankAccount.bankAddress.value"
							:class="{ 'input-error-style': bankAccount.bankAddress.error }"></textarea>
						<span
							v-if="bankAccount.bankAddress.error"
							class="error">Please insert a valid bank address.</span>
					</label>
				</li>

				<li v-if="isClearingCodeAvailable">
					<label>
						{{ clearingCodeName }}
						<span class="req" v-if="bankAccount.bankClearingCode.isRequired">*</span>
						<input
							type="number"
							name="bankClearingCode"
							ref="bankClearingCode"
							max="999999"
							v-model="bankAccount.bankClearingCode.value" />
					</label>
				</li>
			</ul>
			<p v-else>
				Only for wallet using local currency settlement or when you
				have completed our KYB process.
			</p>
		</fieldset>
	</form>
</template>
<script>
import { COUNTRIES } from '../../constants';
import { InfoIcon, CopyIcon } from 'vue-feather-icons';
export default {
	props: {
		isLocalCurrencyWallet: {
			type: Boolean,
			default: false,
		},
		bankAccount: {
			type: Object,
			default: () => ({
				accountName: {
					value: '',
					error: false,
					isRequired: true
				},
				accountNumber: {
					value: '',
					error: false,
					isRequired: true
				},
				bankName: {
					value: '',
					error: false,
					isRequired: true
				},
				bankCountry: {
					value: '',
					error: false,
					isRequired: true
				},
				swiftCode: {
					value: '',
					error: false,
					isRequired: true
				},
				routingNumber: {
					value: '',
					error: false,
					isRequired: true
				},
				bankAddress: {
					value: '',
					error: false,
					isRequired: true
				},
				bankClearingCode: {
					value: '',
					error: false,
					isRequired: true
				},
			}),
		},
		hasBanner: {
			type: Boolean,
			default: false,
		},
		sectionName: {
			type: String,
			default: 'Bank Account',
		},
		legalEntityName: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			// data pool
			countries: COUNTRIES,

			// validation params
			phoneValidation: {
				inputOptions: {
					showDialCode: true,
				},
				placeholder: 'Enter a phone number',
				disabledFetchingCountry: true,
				enabledCountryCode: false,
				validCharactersOnly: true,
			},

			copiedValue: '',
		};
	},
	computed: {
		isClearingCodeAvailable() {
			return this.bankAccount.bankCountry.value === 'AU' || this.bankAccount.bankCountry.value === 'UK';
		},
		clearingCodeName() {
			switch (this.bankAccount.bankCountry.value) {
				case 'AU':
					return 'BSB';
				case 'UK':
					return 'Sort Code';
				default:
					return 'Clearing Code';
			}
		},
	},
	components: {
		InfoIcon,
		CopyIcon,
	},
	methods: {
		copyText(text) {
			this.copyToClipboard(text);
			this.copiedValue = text;
			setTimeout(() => {
				this.copiedValue = '';
			}, 1000);
		}
	}
};
</script>
<style lang="scss" scoped>
.bank-account-form {
	fieldset {
		border: none;
		display: block;

		legend {
			font-size: 24px;
			font-weight: 700;
			margin-bottom: 10px;
		}

		ul {
			margin-top: 12px;

			li {
				margin-bottom: 12px;

				label {
					display: flex;
					flex-wrap: wrap;
					position: relative;

					input,
					select,
					textarea {
						width: 100%;
						display: block;
						min-height: 40px;
						border: 1px solid #ccc;
						border-radius: 5px;

						&.input-error-style {
							border-color: #ff0000;
						}
					}

					textarea {
						font-family: inherit !important;
					}

					span.indicator {
						position: absolute;
						right: 6px;
					}
				}

				p.caption {
					font-size: 12px;
					font-style: italic;

					&>span {
						font-weight: 700;

						&>span {
							width: 100px;
						}

						&:hover {
							cursor: pointer;
						}
					}
				}
			}
		}

		.tooltip {
			position: relative;
			display: inline-block;

			svg {
				margin-left: 6px;
				width: 12px;
				height: 12px;
			}

			&>span {
				visibility: hidden;
				display: flex;
				flex-direction: column;
				background-color: lightslategray;
				width: 275px;
				padding: 10px;
				border-radius: 5px;
				color: #ffffff;
				font-size: 10px;
				text-align: center;
				position: absolute;
				z-index: 1;
				left: 105%;
				top: -20px;
			}

			&[enabled]>span {
				visibility: visible;
			}

			&:hover {
				&>span {
					visibility: visible;
				}
			}
		}
	}

	div.con-vs-alert>div.vs-alert>div {
		display: flex;

		svg {
			height: 24px;
			width: 24px;
		}
	}

	span.error {
		color: red;
		font-size: 10px;
	}

	span.req {
		margin-left: 0.5em;
	}
}
</style>